import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ChangeRoleV2Options,
  ChangeRoleV2Response,
  GetRolesInfoOptions,
  GetRolesInfoResponse,
  SearchTeamOptions,
  SearchTeamResponse,
  UpdateTeamMemberAssignmentsOptions,
  UpdateTeamMemberAssignmentsResponse,
  changeRoleV2 as universalChangeRoleV2,
  getRolesInfo as universalGetRolesInfo,
  removeMember as universalRemoveMember,
  searchTeam as universalSearchTeam,
  updateTeamMemberAssignments as universalUpdateTeamMemberAssignments,
} from './identity-roles-v1-user-users.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/user-management' };

export function removeMember(httpClient: HttpClient): RemoveMemberSignature {
  return (userId: string) =>
    universalRemoveMember(
      userId,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveMemberSignature {
  /**
   * Removes a team member from the requesting account.
   * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
   * @param - User ID of the team member to remove.
   */
  (userId: string): Promise<void>;
}

export function searchTeam(httpClient: HttpClient): SearchTeamSignature {
  return (options?: SearchTeamOptions) =>
    universalSearchTeam(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchTeamSignature {
  /**
   * Retrieves a list of all team members of the requesting account with the specified filtering, sorting, and paging.
   *
   * > **Important**: This call requires an account level API key and can't be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
   *
   * ## Filters
   *
   * When using filters for dates, you must use
   * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
   *
   * ## See also
   *
   * To learn about working with *Search* methods, see
   * [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) and
   * [Sorting and Paging](https://dev.wix.com/docs/rest/articles/get-started/sorting-and-paging).
   * @param - Filter options.
   */
  (options?: SearchTeamOptions): Promise<
    NonNullablePaths<
      SearchTeamResponse,
      | `facets`
      | `facets.${number}.facetType`
      | `teamMembers`
      | `teamMembers.${number}.user._id`
      | `teamMembers.${number}.user.name.firstName`
      | `teamMembers.${number}.user.name.lastName`
      | `teamMembers.${number}.invite._id`
      | `teamMembers.${number}.invite.status`,
      6
    >
  >;
}

export function getRolesInfo(httpClient: HttpClient): GetRolesInfoSignature {
  return (options?: GetRolesInfoOptions) =>
    universalGetRolesInfo(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetRolesInfoSignature {
  /**
   * Retrieves all available roles in the requesting account, including predefined and custom roles.
   * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
   * @param - Filter options.
   */
  (options?: GetRolesInfoOptions): Promise<
    NonNullablePaths<
      GetRolesInfoResponse,
      | `predefinedRoles`
      | `predefinedRoles.${number}._id`
      | `predefinedRoles.${number}.title`
      | `predefinedRoles.${number}.description`
      | `predefinedRoles.${number}.restrictFromLevel`
      | `customRoles`
      | `customRoles.${number}._id`
      | `customRoles.${number}.title`
      | `customRoles.${number}.description`
      | `customRoles.${number}.restrictFromLevel`,
      4
    >
  >;
}

/** @internal */
export function changeRoleV2(httpClient: HttpClient): ChangeRoleV2Signature {
  return (
    userId: string,
    options: NonNullablePaths<
      ChangeRoleV2Options,
      `assignmentIdsToReplace` | `roles`,
      2
    >
  ) =>
    universalChangeRoleV2(
      userId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ChangeRoleV2Signature {
  /**
   * Updates the roles and conditions for an existing team member. Changing roles changes the team member's access to account assets.
   * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
   * @param - ID of team member being affected.
   */
  (
    userId: string,
    options: NonNullablePaths<
      ChangeRoleV2Options,
      `assignmentIdsToReplace` | `roles`,
      2
    >
  ): Promise<
    NonNullablePaths<
      ChangeRoleV2Response,
      | `roles`
      | `roles.${number}.restrictions.resource.resourceType`
      | `roles.${number}.restrictions.resource._id`
      | `roles.${number}.restrictions.site._id`
      | `roles.${number}.subject._id`
      | `roles.${number}.subject.subjectType`,
      6
    >
  >;
}

export function updateTeamMemberAssignments(
  httpClient: HttpClient
): UpdateTeamMemberAssignmentsSignature {
  return (
    userId: string,
    options: NonNullablePaths<
      UpdateTeamMemberAssignmentsOptions,
      `assignmentIdsToRemove` | `newAssignments`,
      2
    >
  ) =>
    universalUpdateTeamMemberAssignments(
      userId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateTeamMemberAssignmentsSignature {
  /**
   * Updates the assignments of roles and conditions for an existing team member.  Changing assignments changes the team member’s access to account assets.
   * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
   * @param - ID of team member being affected.
   * @param - Filter options. The `assignmentIdsToRemove` and `newAssignments` fields **must** be passed.
   */
  (
    userId: string,
    options: NonNullablePaths<
      UpdateTeamMemberAssignmentsOptions,
      `assignmentIdsToRemove` | `newAssignments`,
      2
    >
  ): Promise<
    NonNullablePaths<
      UpdateTeamMemberAssignmentsResponse,
      | `assignments`
      | `assignments.${number}.restrictions.resource.resourceType`
      | `assignments.${number}.restrictions.resource._id`
      | `assignments.${number}.restrictions.site._id`
      | `assignments.${number}.subject._id`
      | `assignments.${number}.subject.subjectType`,
      6
    >
  >;
}

export {
  AccountInfo,
  AccountInvite,
  AccountResourceContext,
  ApiCondition,
  ApiInviteAssignment,
  ApiPolicy,
  ApiResource,
  AreasFilter,
  AssignedPolicy,
  AssignedSubject,
  Assignment,
  AssignmentV3,
  ChangeRoleRequest,
  ChangeRoleResponse,
  ChangeRoleV2Options,
  ChangeRoleV2Request,
  ChangeRoleV2Response,
  CompanionResource,
  Condition,
  ConditionAttributeType,
  ConditionOperator,
  ConditionOperatorOperatorsOneOf,
  ConditionType,
  ConditionTypeOfOneOf,
  ConditionValue,
  ConditionValueValueOneOf,
  Conditions,
  Contributor,
  CreateCustomRoleRequest,
  CreateCustomRoleResponse,
  CustomRole,
  CustomRoleV2,
  DependOnOperator,
  Direction,
  EditorType,
  Effect,
  EnvironmentCondition,
  EnvironmentConditionConditionOneOf,
  EqualOperator,
  ExperimentCondition,
  ExperimentOperator,
  Facet,
  FacetType,
  FacetValue,
  FolderResource,
  FullNameResource,
  FullNameResourceResourceContextOneOf,
  GetPeopleRequest,
  GetPeopleResponse,
  GetPoliciesRequest,
  GetPoliciesResponse,
  GetRolesBackofficeRequest,
  GetRolesBackofficeResponse,
  GetRolesInfoOptions,
  GetRolesInfoRequest,
  GetRolesInfoResponse,
  GetRolesRequest,
  GetRolesResponse,
  GetRolesV2Request,
  GetRolesV2Response,
  GetScopesBackofficeRequest,
  GetScopesBackofficeResponse,
  GetScopesByRoleRequest,
  GetScopesByRoleResponse,
  GetScopesRequest,
  GetScopesResponse,
  GetScopesV2Request,
  GetScopesV2Response,
  GetSubjectsAssignmentsRequest,
  GetSubjectsAssignmentsResponse,
  GetTeamInvitesRequest,
  GetTeamInvitesResponse,
  GetTeamRequest,
  GetTeamResponse,
  GetTeamV2Response,
  GetUsersRequest,
  GetUsersResponse,
  Invite,
  InviteAssignment,
  InviteAssignmentV3,
  InviteResourceAssignment,
  InviteStatus,
  InviteV3,
  JoinedCondition,
  JoinedConditionOperator,
  LikeOperator,
  LocationsRestriction,
  Name,
  OrderField,
  Ordering,
  OrganizationResourceContext,
  Paging,
  People,
  PeopleResource,
  PeopleResourceResourceTypeOneOf,
  PeopleType,
  PermissionScope,
  PermissionScopeV2,
  Person,
  PersonMetaData,
  PersonPersonOneOf,
  Policy,
  PolicyCondition,
  PolicyStatement,
  PolicyType,
  PredefinedRole,
  PredefinedRoleV2,
  PredefinedRoles,
  PredefinedRolesArea,
  PredefinedRolesAreaV2,
  RemoveMemberRequest,
  RemoveMemberResponse,
  Resource,
  ResourceType,
  Restriction,
  RestrictionRestrictionsOneOf,
  Role,
  RoleInfo,
  RoleInfoV2,
  RoleLevel,
  RoleLevelRestriction,
  RoleVisibility,
  RolesInfoFilter,
  ScopeArea,
  ScopeAreaV2,
  ScopeLevel,
  SearchTeamOptions,
  SearchTeamRequest,
  SearchTeamResponse,
  SimpleCondition,
  SimpleConditionOperator,
  SimpleConditionValue,
  SimpleConditionValueValueOneOf,
  SiteResourceContext,
  SiteRestriction,
  Subject,
  SubjectAssignments,
  SubjectContext,
  SubjectContextType,
  SubjectType,
  TeamMember,
  TeamMemberV3,
  TeamMemberV3MembersOneOf,
  UpdateTeamMemberAssignmentsOptions,
  UpdateTeamMemberAssignmentsRequest,
  UpdateTeamMemberAssignmentsResponse,
  User,
  UserV3,
  Visibility,
} from './identity-roles-v1-user-users.universal.js';
